import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ParametrosService } from '../../../services/parametros.service';
import { SweetAlertService } from '../../../services/sweet-alert.service';
import { UserService } from '../../../services/user.service';
import {
  formatPesosCop,
  parsePesosCop,
} from 'src/app/shared/utils/currency-cop.util';
import { PERFIL_COPY } from '../../data/perfil-microempresario.content';
import { PerfilDocumentosService } from '../documentos/perfil-documentos.service';
import {
  PERFIL_DOC_PASO,
  acceptFromExtensiones,
  baseValueKeyFromSlot,
  cantidadSlotsDoc,
  isDeclaracionRentaDoc,
  labelConOpcional,
  intersectSeccionDocsConSolicitud,
  isMultipleDoc,
  pruneStaleDocControls,
  formatBytesLabel,
  slotsForDoc,
} from '../documentos/perfil-documentos.util';
import { aplicaDeclaracionRentaPorRegla } from '../documentos/perfil-renta.util';
import type { PerfilDocSlot } from '../documentos/perfil-documentos.util';
import {
  canRemoveArchivoSlot,
  emptyArchivoSlot,
  isArchivoSlotRechazado,
  isArchivoSlotValidoParaProgreso,
  PerfilArchivoSlotState,
  shouldShowArchivoSlotAprobadoUi,
  slotStateFromUploadResult,
  slotStateFromUploaded,
} from '../documentos/perfil-archivo-slot.util';
import { mapEmpresarioToSobreIngresos } from '../progress/perfil-progress-hydrate';
import {
  PerfilDocumentoDef,
  UploadedTipoDocumento,
} from '../progress/perfil-progress.definitions';
import { PerfilProgressService } from '../progress/perfil-progress.service';
import {
  PERFIL_HUB_ROUTE,
  PERFIL_NAV_SHOW_INTRO,
  PERFIL_PASO_QUERY,
  consumeShowIntroFlag,
  invalidFilledControlKeys,
  markInvalidControlsTouched,
  navigateAfterSectionSave,
  parsePerfilPasoQuery,
  readPerfilNavState,
  resolveNextSectionId,
  resolvePerfilStepCtaKind,
} from '../progress/perfil-section-nav';
import { PerfilSeccionAccesoService } from '../progress/perfil-seccion-acceso.service';
import {
  PerfilSectionDirtyTracker,
  serializePerfilSectionState,
} from '../progress/perfil-section-dirty';
import { PerfilSegmentoService } from '../segmento/perfil-segmento.service';
import { PerfilSegmento } from '../segmento/perfil-segmento.util';
import { isPerfilJuridica } from '../perfil-tipo.util';
import { PerfilDocumentoModalConfig } from '../shared/perfil-documento-modal/perfil-documento-modal.model';
import { PerfilSolicitudService } from '../solicitud/perfil-solicitud.service';
import { AnalyticsTagService } from 'src/app/services/analytics-tag.service';

/** Subpaso dentro de la sección (varios formularios / pestañas). */
interface SobreIngresosStep {
  id: string;
  /** Controles obligatorios que deben validarse para Continuar / Guardar. */
  controls: readonly string[];
}

/** Campos monetarios del flujo de caja (formato COP). */
const FLUJO_MONEDA_KEYS = [
  'ventasUltimoMes',
  'otrosIngresos',
  'pagoProveedores',
  'arriendo',
  'administracion',
  'serviciosPublicosNegocio',
  'sueldoPropio',
  'sueldoEmpleados',
  'planCelular',
  'tarjetaCredito',
  'cuotaCreditos',
  'saldoDeudas',
  'otrosGastos',
] as const;

const GASTOS_SUM_KEYS = [
  'pagoProveedores',
  'arriendo',
  'administracion',
  'serviciosPublicosNegocio',
  'sueldoPropio',
  'sueldoEmpleados',
  'planCelular',
  'tarjetaCredito',
  'cuotaCreditos',
  'otrosGastos',
] as const;

/**
 * Formulario "Sobre los ingresos" — 3 pasos según Figma PN.
 * Paso 2: solo mes + ventas son obligatorios (alineado a DatosFlujoCaja PN).
 */
@Component({
  selector: 'app-perfil-sobre-ingresos',
  templateUrl: './perfil-sobre-ingresos.component.html',
  styleUrls: ['./perfil-sobre-ingresos.component.scss'],
  standalone: false,
})
export class PerfilSobreIngresosComponent implements OnInit, OnDestroy {
  readonly copy = PERFIL_COPY;
  readonly archivoIcon =
    'assets/img/perfil/icon-archivo-cargado.svg';
  readonly checkIcon = 'assets/img/perfil/icon-check.svg';
  readonly iconIngresos = 'assets/img/perfil/icon-flujo-ingresos.svg';
  readonly iconGastos = 'assets/img/perfil/icon-flujo-gastos.svg';
  readonly iconEfectivo = 'assets/img/perfil/icon-flujo-efectivo.svg';

  userName = '';
  form!: UntypedFormGroup;
  docsFiles: Record<string, PerfilArchivoSlotState> = {};
  /** Docs del paso activos/soportes (pasoId documentos → alias). */
  documentosPasoActivos: PerfilDocumentoDef[] = [];
  /** Docs del paso info-adicional. */
  documentosPasoInfo: PerfilDocumentoDef[] = [];
  introModalOpen = false;
  salirModalOpen = false;
  docModalOpen = false;
  docModalConfig: PerfilDocumentoModalConfig | null = null;
  private docModalValueKey: string | null = null;
  private pendingDocInput: HTMLInputElement | null = null;
  /** Título del modal de políticas ('R' = centrales de riesgo), igual que solicitud PN. */
  titlePoliticasModal = 'R';

  /** Acordeones del flujo de caja. */
  panelIngresosOpen = true;
  panelGastosOpen = false;

  steps: SobreIngresosStep[] = [];
  currentStep = 0;
  saving = false;
  private readonly dirtyTracker = new PerfilSectionDirtyTracker();

  anios: Array<{ value: string; label: string }> = [];

  readonly mesesMock = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  @ViewChild('sectionScroll') sectionScroll?: ElementRef<HTMLElement>;
  @ViewChildren('docInput') docInputs?: QueryList<ElementRef<HTMLInputElement>>;

  private readonly showIntroOnEnter: boolean;
  private progressSub?: Subscription;
  private docsSub?: Subscription;
  private modalSub?: Subscription;
  private readonly loadSub = new Subscription();
  private pendingPaso: number | null = null;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly progress: PerfilProgressService,
    private readonly segmentoService: PerfilSegmentoService,
    private readonly documentosService: PerfilDocumentosService,
    private readonly solicitudService: PerfilSolicitudService,
    private readonly seccionAcceso: PerfilSeccionAccesoService,
    private readonly parametros: ParametrosService,
    private readonly sweetAlert: SweetAlertService,
    private readonly analyticsTag: AnalyticsTagService
  ) {
    this.showIntroOnEnter = !!readPerfilNavState(this.router)[
      PERFIL_NAV_SHOW_INTRO
    ];
  }

  ngOnInit(): void {
    this.userName = this.userService.getUser()?.nombre ?? '';
    this.rebuildSteps();
    // Mes anterior al del diligenciamiento (ene → dic).
    const mesAnteriorIdx = (new Date().getMonth() + 11) % 12;
    const mesDefault = this.mesesMock[mesAnteriorIdx] ?? 'Febrero';
    this.form = this.fb.group({
      anioActivos: ['', Validators.required],
      valorActivos: ['', Validators.required],
      ventasUltimoAnio: [''],
      declaraRenta: [''],
      autorizacionCentrales: [false, Validators.requiredTrue],
      mesReporte: [mesDefault],
      ventasUltimoMes: [''],
      otrosIngresos: [''],
      fuenteOtrosIngresos: [''],
      pagoProveedores: [''],
      arriendo: [''],
      administracion: [''],
      serviciosPublicosNegocio: [''],
      sueldoPropio: [''],
      sueldoEmpleados: [''],
      planCelular: [''],
      tarjetaCredito: [''],
      tieneCreditos: [''],
      cuotaCreditos: [''],
      saldoDeudas: [''],
      otrosGastos: [''],
      fuenteOtrosGastos: [''],
      modoGastos: ['generales'],
      gastosGeneralesMonto: [''],
      observaciones: [''],
    });
    this.docsSub = this.documentosService.ensureLoaded().subscribe((docs) => {
      this.applyDocumentos(
        docs.filter((d) => d.seccionId === 'sobre-ingresos')
      );
      this.precargarDesdeBackend();
    });
    this.cargarAnios();
    this.applySegmentoValidators();
    this.syncProgress();
    this.progressSub = this.form.valueChanges.subscribe(() => {
      this.syncProgress();
    });
    this.progressSub.add(
      this.form
        .get('tieneCreditos')!
        .valueChanges.subscribe((value) => this.syncCreditosFields(value))
    );
    this.progressSub.add(
      this.form.get('declaraRenta')!.valueChanges.subscribe(() => {
        this.syncDeclaracionRentaValidators();
        this.rebuildSteps();
        this.syncProgress();
      })
    );
    this.progressSub.add(
      this.form.get('modoGastos')!.valueChanges.subscribe(() => {
        this.syncGastosGeneralesValidators();
        this.rebuildSteps();
        this.syncProgress();
      })
    );
    this.progressSub.add(
      this.segmentoService.ensureParamsLoaded().subscribe(() => {
        this.rebuildSteps();
        this.applySegmentoValidators();
        this.syncDeclaracionRentaValidators();
        this.syncProgress();
      })
    );
    this.loadSub.add(
      this.route.queryParamMap.subscribe((params) => {
        this.pendingPaso = parsePerfilPasoQuery(params.get(PERFIL_PASO_QUERY));
        this.applyPendingPaso();
      })
    );
  }

  ngOnDestroy(): void {
    this.progressSub?.unsubscribe();
    this.docsSub?.unsubscribe();
    this.modalSub?.unsubscribe();
    this.loadSub.unsubscribe();
  }

  private applyDocumentos(docs: PerfilDocumentoDef[]): void {
    this.documentosPasoActivos = docs
      .filter(
        (d) =>
          !d.pasoId ||
          d.pasoId === PERFIL_DOC_PASO.documentos ||
          d.pasoId === PERFIL_DOC_PASO.fotos ||
          d.pasoId === 'activos-soportes' ||
          !!d.esFoto
      )
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
    this.documentosPasoInfo = docs
      .filter((d) => d.pasoId === PERFIL_DOC_PASO.infoAdicional)
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
    const allDocs = [
      ...this.documentosPasoActivos,
      ...this.documentosPasoInfo,
    ];
    pruneStaleDocControls(this.form, this.docsFiles, allDocs);
    this.pruneStaleDocSlots(allDocs);

    for (const doc of allDocs) {
      const requiredInicial =
        isDeclaracionRentaDoc(doc) && !this.esJuridica
          ? false
          : doc.obligatorio;
      if (!this.form.contains(doc.valueKey)) {
        this.form.addControl(
          doc.valueKey,
          this.fb.control('', requiredInicial ? Validators.required : [])
        );
      } else {
        const ctrl = this.form.get(doc.valueKey);
        if (requiredInicial) {
          ctrl?.setValidators([Validators.required]);
        } else if (this.esJuridica || !isDeclaracionRentaDoc(doc)) {
          ctrl?.clearValidators();
        }
        ctrl?.updateValueAndValidity({ emitEvent: false });
      }
      for (const slot of slotsForDoc(doc)) {
        if (!this.docsFiles[slot.slotKey]) {
          this.docsFiles[slot.slotKey] = emptyArchivoSlot();
        }
      }
    }
    this.rebuildSteps();
    this.syncDeclaracionRentaValidators();
    this.syncProgress();
  }

  /** PN: docs del paso activos sin renta (renta va gated por radios/regla). PJ: catálogo tal cual. */
  get documentosPasoActivosSinRenta(): PerfilDocumentoDef[] {
    if (this.esJuridica) {
      return this.documentosPasoActivos;
    }
    return this.documentosPasoActivos.filter((d) => !isDeclaracionRentaDoc(d));
  }

  /** PN: renta no va a opcionales (pregunta/regla). PJ: sí, si PAR-117 la deja opcional. */
  get documentosPasoInfoSinRenta(): PerfilDocumentoDef[] {
    if (this.esJuridica) {
      return this.documentosPasoInfo;
    }
    return this.documentosPasoInfo.filter((d) => !isDeclaracionRentaDoc(d));
  }

  get docDeclaracionRenta(): PerfilDocumentoDef | null {
    return (
      this.documentosPasoActivos.find((d) => isDeclaracionRentaDoc(d)) ??
      this.documentosPasoInfo.find((d) => isDeclaracionRentaDoc(d)) ??
      null
    );
  }

  /**
   * PAR-096 (antigüedad + fechaRegistro) o PAR-117 (valorVentas).
   * Si fecha es null (sin cámara), la regla de antigüedad no aplica.
   */
  get aplicaDeclaracionRentaPorRegla(): boolean {
    if (this.esJuridica || this.esExpress || !this.docDeclaracionRenta) {
      return false;
    }
    const emp = this.solicitudService.empresarioSnapshot;
    const p = this.segmentoService.paramsSnapshot;
    return aplicaDeclaracionRentaPorRegla({
      fechaRegistroActividadEconomica:
        emp?.fechaRegistroActividadEconomica ?? null,
      valorVentas: emp?.valorVentas ?? null,
      tiempoMinimoMeses: p.tiempoMinimoDeclaracionRenta,
      valorLimiteVentas: p.valorLimiteVentas,
    });
  }

  /** Pregunta Si/No solo si la regla automática no exige el documento. */
  get muestraPreguntaDeclaraRenta(): boolean {
    return (
      !this.esJuridica &&
      !this.esExpress &&
      !!this.docDeclaracionRenta &&
      !this.aplicaDeclaracionRentaPorRegla
    );
  }

  /**
   * Upload visible si aplica por regla (obligatorio sin pregunta)
   * o si el usuario respondió Sí.
   */
  get muestraDocDeclaracionRenta(): boolean {
    if (this.esJuridica || this.esExpress || !this.docDeclaracionRenta) {
      return false;
    }
    if (this.aplicaDeclaracionRentaPorRegla) {
      return true;
    }
    return this.form?.get('declaraRenta')?.value === 'si';
  }

  labelDocRenta(doc: PerfilDocumentoDef): string {
    return labelConOpcional(doc.label, true);
  }

  private pruneStaleDocSlots(keepDocs: ReadonlyArray<PerfilDocumentoDef>): void {
    const keep = new Set<string>();
    for (const doc of keepDocs) {
      for (const slot of slotsForDoc(doc)) {
        keep.add(slot.slotKey);
      }
    }
    for (const key of Object.keys(this.docsFiles)) {
      if (key.startsWith('doc_') && !keep.has(key)) {
        delete this.docsFiles[key];
      }
    }
  }

  slotsFor(doc: PerfilDocumentoDef): PerfilDocSlot[] {
    return slotsForDoc(doc);
  }

  isDocMultiple(doc: PerfilDocumentoDef): boolean {
    return isMultipleDoc(doc);
  }

  maxSlotsCount(doc: PerfilDocumentoDef): number {
    return cantidadSlotsDoc(doc);
  }

  filledSlots(doc: PerfilDocumentoDef): PerfilDocSlot[] {
    return slotsForDoc(doc).filter((s) => this.hasSlotDoc(s.slotKey));
  }

  filledSlotsCount(doc: PerfilDocumentoDef): number {
    return this.filledSlots(doc).length;
  }

  nextEmptySlot(doc: PerfilDocumentoDef): PerfilDocSlot | null {
    return slotsForDoc(doc).find((s) => !this.hasSlotDoc(s.slotKey)) ?? null;
  }

  multiUploadCta(doc: PerfilDocumentoDef): string {
    return this.filledSlotsCount(doc) === 0
      ? this.copy.sobreIngresos.cargarArchivos
      : this.copy.sobreIngresos.cargarOtroArchivo;
  }

  multiUploadHint(doc: PerfilDocumentoDef): string {
    return this.filledSlotsCount(doc) === 0
      ? this.copy.sobreIngresos.puedesSubirVarios
      : this.copy.sobreIngresos.archivosCargados;
  }

  acceptFor(doc: PerfilDocumentoDef): string {
    return acceptFromExtensiones(doc.extensionesValidas);
  }

  labelDoc(doc: PerfilDocumentoDef): string {
    return labelConOpcional(doc.label, doc.obligatorio);
  }

  private findDocBySlotOrValueKey(
    slotOrValueKey: string
  ): PerfilDocumentoDef | undefined {
    const base = baseValueKeyFromSlot(slotOrValueKey);
    return (
      this.documentosService.findByValueKey(base) ??
      this.documentosPasoActivos.find((d) => d.valueKey === base) ??
      this.documentosPasoInfo.find((d) => d.valueKey === base)
    );
  }

  /** Progreso / validación: ≥1 slot con archivo válido (no rechazado). */
  hasDoc(valueKey: string): boolean {
    const doc = this.findDocBySlotOrValueKey(valueKey);
    if (!doc) {
      return isArchivoSlotValidoParaProgreso(this.docsFiles[valueKey]);
    }
    return slotsForDoc(doc).some((s) =>
      isArchivoSlotValidoParaProgreso(this.docsFiles[s.slotKey])
    );
  }

  hasSlotDoc(slotKey: string): boolean {
    return !!this.docsFiles[slotKey]?.name;
  }

  isSlotRechazado(slotKey: string): boolean {
    return isArchivoSlotRechazado(this.docsFiles[slotKey]);
  }

  isSlotAprobadoUi(slotKey: string): boolean {
    return shouldShowArchivoSlotAprobadoUi(
      this.docsFiles[slotKey],
      this.solicitudService.idEstadoSolicitud
    );
  }

  canRemoveSlot(slotKey: string): boolean {
    return canRemoveArchivoSlot(
      this.docsFiles[slotKey],
      this.solicitudService.idEstadoSolicitud
    );
  }

  slotStatusLabel(slotKey: string): string {
    if (this.isSlotRechazado(slotKey)) {
      return this.copy.sobreIngresos.archivoRechazado;
    }
    if (this.isSlotAprobadoUi(slotKey)) {
      return this.copy.sobreIngresos.archivoAprobado;
    }
    return this.copy.sobreIngresos.archivoSubido;
  }

  slotObservacion(slotKey: string): string {
    return String(this.docsFiles[slotKey]?.observacionDocumentoOCR || '').trim();
  }

  private syncDocProgressControl(doc: PerfilDocumentoDef): void {
    const filled = slotsForDoc(doc).find((s) =>
      isArchivoSlotValidoParaProgreso(this.docsFiles[s.slotKey])
    );
    const name = filled ? this.docsFiles[filled.slotKey].name : '';
    this.form.get(doc.valueKey)?.setValue(name, { emitEvent: false });
    this.form.get(doc.valueKey)?.markAsTouched();
    this.form.get(doc.valueKey)?.updateValueAndValidity({ emitEvent: false });
  }

  openDocModalFor(
    doc: PerfilDocumentoDef,
    inputEl?: HTMLInputElement,
    slotKeyForUpload?: string
  ): void {
    const resolvedSlotKey = slotKeyForUpload || doc.valueKey;
    this.analyticsTag.trackCtaClick({
      ctaLabel: doc.label,
      ctaSection: 'sobre-ingresos',
      ctaComponent: 'perfil-sobre-ingresos',
      ctaElementId: `btn-perfil-doc-${resolvedSlotKey}`,
      hu: '8099',
    });
    this.modalSub?.unsubscribe();
    this.docModalValueKey = slotKeyForUpload || doc.valueKey;
    this.pendingDocInput = inputEl ?? null;
    this.modalSub = this.documentosService.modalConfigFor(doc).subscribe((cfg) => {
      this.docModalConfig = cfg;
      this.docModalOpen = true;
    });
  }

  get sectionPercent(): number {
    return this.progress.getSectionProgress('sobre-ingresos').percent;
  }

  get esExpress(): boolean {
    return this.segmentoService.esExpress;
  }

  get esJuridica(): boolean {
    return isPerfilJuridica(this.userService.getUser()?.tipoPersona);
  }

  get segmentoIntro(): PerfilSegmento {
    return this.segmentoService.segmento;
  }

  get muestraFlujoCaja(): boolean {
    return this.segmentoService.muestraFlujoCaja;
  }

  get currentStepId(): string {
    return this.steps[this.currentStep]?.id ?? '';
  }

  get isLastStep(): boolean {
    return this.currentStep >= this.steps.length - 1;
  }

  /** PN: docs en activos. PJ: docs en paso `documentos`. */
  get muestraDocsObligatoriosEnPaso(): boolean {
    if (this.esJuridica) {
      return this.currentStepId === 'documentos';
    }
    return this.currentStepId === 'activos-soportes';
  }

  get sectionDirty(): boolean {
    return this.dirtyTracker.isDirty(() => this.serializeSectionState());
  }

  get ctaKind(): 'continuar' | 'guardar' {
    return resolvePerfilStepCtaKind(
      this.isLastStep,
      this.sectionPercent,
      this.sectionDirty
    );
  }

  get ctaLabel(): string {
    return this.ctaKind === 'continuar'
      ? this.copy.botones.continuar
      : this.copy.botones.guardarInformacion;
  }

  get continuarDisabled(): boolean {
    if (this.saving) {
      return true;
    }
    if (this.isLastStep && this.ctaKind === 'continuar') {
      return false;
    }
    return !this.isCurrentStepValid();
  }

  get muestraCreditosDetalle(): boolean {
    return this.form?.get('tieneCreditos')?.value === 'si';
  }

  get esGastosGenerales(): boolean {
    return this.form?.get('modoGastos')?.value === 'generales';
  }

  get esGastosEspecificos(): boolean {
    return !this.esGastosGenerales;
  }

  get mesReporteLabel(): string {
    const mes = this.form?.get('mesReporte')?.value;
    return typeof mes === 'string' && mes ? mes.toLowerCase() : 'mes';
  }

  get ventasLabel(): string {
    return `${this.copy.sobreIngresos.campos.ventasUltimoMes} (${this.mesReporteLabel})`;
  }

  get totalIngresosNum(): number {
    return (
      parsePesosCop(this.form?.get('ventasUltimoMes')?.value) +
      parsePesosCop(this.form?.get('otrosIngresos')?.value)
    );
  }

  get totalGastosNum(): number {
    if (this.esGastosGenerales) {
      return parsePesosCop(this.form?.get('gastosGeneralesMonto')?.value);
    }
    return GASTOS_SUM_KEYS.reduce(
      (acc, key) => acc + parsePesosCop(this.form?.get(key)?.value),
      0
    );
  }

  get efectivoDisponibleNum(): number {
    return this.totalIngresosNum - this.totalGastosNum;
  }

  get totalIngresosLabel(): string {
    return formatPesosCop(this.totalIngresosNum) || '$0';
  }

  get totalGastosLabel(): string {
    const n = this.totalGastosNum;
    if (!n) {
      return '-$0';
    }
    return `-${formatPesosCop(n)}`;
  }

  get efectivoDisponibleLabel(): string {
    const n = this.efectivoDisponibleNum;
    if (n < 0) {
      return `-${formatPesosCop(Math.abs(n))}`;
    }
    return formatPesosCop(n) || '$0';
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  errorMessage(_controlName: string): string {
    return this.copy.sobreIngresos.validaciones.campoObligatorio;
  }

  togglePanelIngresos(): void {
    this.panelIngresosOpen = !this.panelIngresosOpen;
  }

  togglePanelGastos(): void {
    this.panelGastosOpen = !this.panelGastosOpen;
  }

  setModoGastos(modo: 'generales' | 'especificos'): void {
    this.form.get('modoGastos')?.setValue(modo, { emitEvent: true });
  }

  onMonedaInput(controlName: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.form
      .get(controlName)
      ?.setValue(formatPesosCop(input.value), { emitEvent: true });
  }

  onMonedaBlur(controlName: string): void {
    const ctrl = this.form.get(controlName);
    if (!ctrl) {
      return;
    }
    ctrl.setValue(formatPesosCop(ctrl.value), { emitEvent: true });
  }

  /** Alias usado por el campo valorActivos del paso 1. */
  onValorActivosInput(event: Event): void {
    this.onMonedaInput('valorActivos', event);
  }

  onValorActivosBlur(): void {
    this.onMonedaBlur('valorActivos');
  }

  onVentasUltimoAnioInput(event: Event): void {
    this.onMonedaInput('ventasUltimoAnio', event);
  }

  onVentasUltimoAnioBlur(): void {
    this.onMonedaBlur('ventasUltimoAnio');
  }

  openDocModal(valueKey: string, inputEl?: HTMLInputElement): void {
    const doc = this.findDocBySlotOrValueKey(valueKey);
    if (doc) {
      this.openDocModalFor(doc, inputEl, valueKey);
      return;
    }
    this.modalSub?.unsubscribe();
    this.docModalValueKey = valueKey;
    this.pendingDocInput = inputEl ?? null;
    this.modalSub = this.documentosService
      .modalConfigForValueKey(baseValueKeyFromSlot(valueKey), {
        label: 'Documento',
        seccionId: 'sobre-ingresos',
      })
      .subscribe((cfg) => {
        this.docModalConfig = cfg;
        this.docModalOpen = true;
      });
  }

  closeDocModal(): void {
    this.docModalOpen = false;
    this.docModalConfig = null;
    this.docModalValueKey = null;
    this.pendingDocInput = null;
  }

  onSubirDesdeDocModal(): void {
    if (!this.docModalValueKey) {
      return;
    }
    this.triggerDocUpload(this.docModalValueKey, this.pendingDocInput ?? undefined);
  }

  triggerDocUpload(key: string, inputEl?: HTMLInputElement): void {
    if (inputEl) {
      inputEl.value = '';
      inputEl.click();
      return;
    }
    const input = this.docInputs?.get(this.docInputIndex(key))?.nativeElement;
    if (!input) {
      return;
    }
    input.value = '';
    input.click();
  }

  onDocSelected(slotOrValueKey: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    const doc = this.findDocBySlotOrValueKey(slotOrValueKey);
    const slotKey = slotOrValueKey;
    if (!doc) {
      this.docsFiles[slotKey] = {
        name: file.name,
        sizeLabel: formatBytesLabel(file.size),
      };
      this.form
        .get(baseValueKeyFromSlot(slotKey))
        ?.setValue(file.name, { emitEvent: false });
      if (this.docModalOpen && this.docModalValueKey === slotKey) {
        this.closeDocModal();
      }
      return;
    }
    this.solicitudService
      .subirDocumento(file, doc, {
        esFoto: !!doc.esFoto,
        excludeNombreArchivo: this.docsFiles[slotKey]?.name || undefined,
      })
      .subscribe((res) => {
        if (!res.exitoso) {
          void this.sweetAlert.errorModal(
            'Error!',
            true,
            res.error || 'No se pudo subir el archivo'
          );
          return;
        }
        if (res.estadoDocumentoOCR === 0) {
          void this.sweetAlert.errorModal(
            'Error!',
            true,
            res.error ||
              res.observacionDocumentoOCR ||
              this.copy.sobreIngresos.validaciones.documentosInconsistentes
          );
          this.docsFiles[slotKey] = slotStateFromUploadResult(file, res);
          this.syncDocProgressControl(doc);
          this.syncProgress();
          this.solicitudService.persistirCamposProgreso().subscribe();
          return;
        }
        void this.sweetAlert.successModal('Archivo subido');
        this.docsFiles[slotKey] = slotStateFromUploadResult(file, res);
        this.syncDocProgressControl(doc);
        if (this.docModalOpen && this.docModalValueKey === slotKey) {
          this.closeDocModal();
        }
        this.syncProgress();
        this.solicitudService.persistirCamposProgreso().subscribe();
      });
  }

  removeDoc(slotOrValueKey: string, inputEl?: HTMLInputElement): void {
    const doc = this.findDocBySlotOrValueKey(slotOrValueKey);
    const slotKey = slotOrValueKey;
    if (!this.canRemoveSlot(slotKey)) {
      return;
    }
    const idDocumento = this.docsFiles[slotKey]?.idDocumento;
    const clearLocal = (): void => {
      this.docsFiles[slotKey] = emptyArchivoSlot();
      if (doc) {
        this.syncDocProgressControl(doc);
      } else {
        this.form
          .get(baseValueKeyFromSlot(slotKey))
          ?.setValue('', { emitEvent: false });
      }
      if (inputEl) {
        inputEl.value = '';
      }
      this.syncProgress();
    };
    if (idDocumento) {
      this.solicitudService
        .eliminarDocumento(idDocumento, {
          esFoto: !!doc?.esFoto,
          idTipoDocumento: doc?.idTipoDocumento ?? doc?.id,
          nombreArchivo: this.docsFiles[slotKey]?.name || null,
        })
        .subscribe((res) => {
          if (!res.exitoso) {
            void this.sweetAlert.errorModal(
              'Error!',
              true,
              res.error || 'No se pudo eliminar el documento'
            );
            return;
          }
          clearLocal();
          this.solicitudService.persistirCamposProgreso().subscribe();
        });
      return;
    }
    clearLocal();
    this.solicitudService.persistirCamposProgreso().subscribe();
  }

  onCta(): void {
    if (this.saving) {
      return;
    }
    this.markCurrentStepTouched();
    if (this.ctaKind !== 'continuar' || !this.isLastStep) {
      if (!this.isCurrentStepValid()) {
        return;
      }
    }
    this.syncProgress();
    if (!this.sectionDirty) {
      if (this.isLastStep) {
        this.goToNextSection();
        return;
      }
      this.currentStep += 1;
      if (this.currentStepId === 'flujo-caja') {
        this.panelIngresosOpen = true;
        this.panelGastosOpen = false;
      }
      this.scrollFormToTop();
      return;
    }
    this.guardarPasoYAvanzar();
  }

  onSaltar(): void {
    this.salirModalOpen = true;
  }

  closeSalirModal(): void {
    this.salirModalOpen = false;
  }

  onSalirGuardar(): void {
    this.salirModalOpen = false;
    this.syncProgress();
    this.guardarPatchActual().subscribe(() => {
      void this.router.navigateByUrl(PERFIL_HUB_ROUTE);
    });
  }

  closeIntroModal(): void {
    this.introModalOpen = false;
  }

  onIntroCompletarDatos(): void {
    this.closeIntroModal();
  }

  openPoliticasCentrales(): void {
    this.titlePoliticasModal = 'R';
  }

  private maybeOpenIntroModal(): void {
    if (!this.showIntroOnEnter) {
      return;
    }
    if (!this.seccionAcceso.shouldShowIntro('sobre-ingresos')) {
      consumeShowIntroFlag(readPerfilNavState(this.router));
      return;
    }
    this.seccionAcceso.markIntroShown('sobre-ingresos');
    this.introModalOpen = true;
    consumeShowIntroFlag(readPerfilNavState(this.router));
  }

  private rebuildSteps(): void {
    if (this.esJuridica) {
      this.rebuildStepsJuridica();
      return;
    }
    const docKeys = this.documentosPasoActivosSinRenta
      .filter((d) => d.obligatorio)
      .map((d) => d.valueKey);
    const rentaDoc = this.docDeclaracionRenta;
    if (this.muestraDocDeclaracionRenta && rentaDoc) {
      docKeys.push(rentaDoc.valueKey);
    }
    const activosControls = [
      'anioActivos',
      'valorActivos',
      ...docKeys,
      ...(this.muestraPreguntaDeclaraRenta ? ['declaraRenta'] : []),
      'autorizacionCentrales',
    ];
    const next: SobreIngresosStep[] = [
      { id: 'activos-soportes', controls: activosControls },
    ];
    if (this.muestraFlujoCaja) {
      const flujoControls = ['mesReporte', 'ventasUltimoMes'];
      if (this.esGastosGenerales) {
        flujoControls.push('gastosGeneralesMonto');
      }
      next.push({
        id: 'flujo-caja',
        controls: flujoControls,
      });
    }
    next.push({
      id: 'info-adicional',
      controls: this.documentosPasoInfoSinRenta
        .filter((d) => d.obligatorio)
        .map((d) => d.valueKey),
    });
    this.applyRebuiltSteps(next);
  }

  /** PJ datos → documentos obligatorios → opcionales. */
  private rebuildStepsJuridica(): void {
    const docKeys = this.documentosPasoActivos
      .filter((d) => d.obligatorio)
      .map((d) => d.valueKey);
    this.applyRebuiltSteps([
      {
        id: 'activos-soportes',
        controls: [
          'anioActivos',
          'ventasUltimoAnio',
          'valorActivos',
          'autorizacionCentrales',
        ],
      },
      { id: 'documentos', controls: docKeys },
      {
        id: 'info-adicional',
        controls: this.documentosPasoInfoSinRenta
          .filter((d) => d.obligatorio)
          .map((d) => d.valueKey),
      },
    ]);
  }

  private applyRebuiltSteps(next: SobreIngresosStep[]): void {
    this.steps = next;
    if (this.pendingPaso != null) {
      this.applyPendingPaso();
      return;
    }
    if (this.currentStep > this.steps.length - 1) {
      this.currentStep = Math.max(0, this.steps.length - 1);
    }
  }

  private applyPendingPaso(): void {
    if (this.pendingPaso == null || !this.steps.length) {
      return;
    }
    const nextStep = Math.min(this.pendingPaso, this.steps.length - 1);
    this.pendingPaso = null;
    if (nextStep === this.currentStep) {
      return;
    }
    this.currentStep = nextStep;
    this.scrollFormToTop();
  }

  /** Validators de renta/flujo según Express + PAR-128. */
  private applySegmentoValidators(): void {
    const setReq = (key: string, required: boolean) => {
      const ctrl = this.form.get(key);
      if (!ctrl) {
        return;
      }
      if (required) {
        ctrl.setValidators([Validators.required]);
      } else {
        ctrl.clearValidators();
      }
      ctrl.updateValueAndValidity({ emitEvent: false });
    };

    setReq('declaraRenta', this.muestraPreguntaDeclaraRenta);
    setReq('mesReporte', this.muestraFlujoCaja);
    setReq('ventasUltimoMes', this.muestraFlujoCaja);
    setReq('ventasUltimoAnio', this.esJuridica);

    if (this.esExpress) {
      this.form.get('declaraRenta')?.setValue('', { emitEvent: false });
    } else if (!this.esJuridica && this.aplicaDeclaracionRentaPorRegla) {
      // Sin pregunta: fijamos Sí para persistencia / progreso.
      this.form.get('declaraRenta')?.setValue('si', { emitEvent: false });
    }
    this.syncDeclaracionRentaValidators();
    this.syncGastosGeneralesValidators();
  }

  /** Total de gastos obligatorio solo en modo gastos generales. */
  private syncGastosGeneralesValidators(): void {
    const ctrl = this.form.get('gastosGeneralesMonto');
    if (!ctrl) {
      return;
    }
    if (this.muestraFlujoCaja && this.esGastosGenerales) {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * PJ: obligatoriedad = flag del catálogo (sin reglas PN).
   * PN: obligatorio si aplica por regla o respondió Sí (ignora opcional del catálogo).
   */
  private syncDeclaracionRentaValidators(): void {
    const doc = this.docDeclaracionRenta;
    if (!doc) {
      return;
    }
    const ctrl = this.form.get(doc.valueKey);
    if (!ctrl) {
      return;
    }
    const required = this.esJuridica
      ? !!doc.obligatorio
      : this.muestraDocDeclaracionRenta;
    if (required) {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  /** Si No / vacío: limpia cuota y saldo (siguen opcionales). */
  private syncCreditosFields(value: unknown): void {
    if (value === 'si') {
      return;
    }
    this.form.get('cuotaCreditos')?.setValue('', { emitEvent: false });
    this.form.get('saldoDeudas')?.setValue('', { emitEvent: false });
  }

  private isCurrentStepValid(): boolean {
    const step = this.steps[this.currentStep];
    if (!step) {
      return false;
    }
    return step.controls.every((key) => this.form.get(key)?.valid);
  }

  private markCurrentStepTouched(): void {
    const step = this.steps[this.currentStep];
    if (!step) {
      return;
    }
    for (const key of step.controls) {
      this.form.get(key)?.markAsTouched();
    }
  }

  private goToNextSection(): void {
    const nextId = resolveNextSectionId('sobre-ingresos');
    const nextPercent = nextId
      ? Math.max(
          this.seccionAcceso.sectionPercent(nextId),
          this.progress.getSectionProgress(nextId).percent
        )
      : 0;
    navigateAfterSectionSave(this.router, 'sobre-ingresos', nextPercent);
  }

  private scrollFormToTop(): void {
    const el = this.sectionScroll?.nativeElement;
    if (el) {
      el.scrollTop = 0;
    }
  }

  private syncProgress(): void {
    const emp = this.solicitudService.empresarioSnapshot;
    this.progress.updateSectionValues(
      'sobre-ingresos',
      {
        ...this.form.getRawValue(),
        fechaRegistroActividadEconomica:
          emp?.fechaRegistroActividadEconomica ?? null,
        valorVentas: emp?.valorVentas ?? null,
        aplicaDeclaracionRenta: this.aplicaDeclaracionRentaPorRegla,
      },
      invalidFilledControlKeys(this.form)
    );
  }

  /** Orden de inputs file visibles en el DOM del paso actual (ViewChildren). */
  private docInputIndex(key: string): number {
    const list =
      this.currentStepId === 'info-adicional'
        ? this.documentosPasoInfo
        : this.documentosPasoActivos;
    return list.findIndex((d) => d.valueKey === key);
  }

  private cargarAnios(): void {
    const tipo = this.esJuridica ? 'juridica' : 'natural';
    this.parametros.getAnios(tipo).subscribe({
      next: (data: any) => {
        const list: number[] = data?.anios ?? [];
        this.anios = list
          .filter((anio) => Number(anio) > 0)
          .map((anio) => ({ value: String(anio), label: String(anio) }));
        if (!this.form.get('anioActivos')?.value && this.anios.length) {
          this.form
            .get('anioActivos')
            ?.setValue(this.anios[0].value, { emitEvent: false });
        }
      },
      error: () => {
        const y = new Date().getFullYear();
        this.anios = [
          { value: String(y - 1), label: String(y - 1) },
          { value: String(y - 2), label: String(y - 2) },
          { value: String(y - 3), label: String(y - 3) },
        ];
        if (!this.form.get('anioActivos')?.value && this.anios.length) {
          this.form
            .get('anioActivos')
            ?.setValue(this.anios[0].value, { emitEvent: false });
        }
      },
    });
  }

  private precargarDesdeBackend(): void {
    this.loadSub.add(
      this.solicitudService
        .ensureCargado(true)
        .pipe(
          switchMap((carga) =>
            this.solicitudService
              .cargarObservacionesMicroempresario()
              .pipe(map((observaciones) => ({ carga, observaciones })))
          )
        )
        .subscribe(({ carga, observaciones }) => {
          const emp = carga.empresario;
          if (!emp) {
            if (observaciones != null) {
              this.form.patchValue(
                { observaciones },
                { emitEvent: false }
              );
            }
            this.precargarDocumentosCargados();
            return;
          }
          const patch = { ...mapEmpresarioToSobreIngresos(emp) };
          const monedaKeys = [
            'valorActivos',
            'ventasUltimoAnio',
            'gastosGeneralesMonto',
            ...FLUJO_MONEDA_KEYS,
          ] as const;
          for (const key of monedaKeys) {
            if (patch[key] != null && patch[key] !== '') {
              patch[key] = formatPesosCop(patch[key]);
            }
          }
          patch['autorizacionCentrales'] =
            !!carga.solicitud?.apectacionCentrales;
          if (observaciones != null) {
            patch['observaciones'] = observaciones;
          }
          this.form.patchValue(patch, { emitEvent: false });
          this.syncCreditosFields(this.form.get('tieneCreditos')?.value);
          this.hydrateModoGastosDesdeMontos();
          this.applySegmentoValidators();
          this.rebuildSteps();
          this.syncProgress();
          this.precargarDocumentosCargados();
        })
    );
  }

  /**
   * Si hay desglose de gastos específicos → modo específicos;
   * si solo hay total en "otros" con fuente generales (o vacío) → generales.
   */
  private hydrateModoGastosDesdeMontos(): void {
    const especificosKeys = [
      'pagoProveedores',
      'arriendo',
      'administracion',
      'serviciosPublicosNegocio',
      'sueldoPropio',
      'sueldoEmpleados',
      'planCelular',
      'tarjetaCredito',
      'cuotaCreditos',
    ] as const;
    const hasEspecificos = especificosKeys.some(
      (k) => parsePesosCop(this.form.get(k)?.value) > 0
    );
    if (hasEspecificos) {
      this.form.get('modoGastos')?.setValue('especificos', { emitEvent: false });
      return;
    }
    this.form.get('modoGastos')?.setValue('generales', { emitEvent: false });
    const otros = parsePesosCop(this.form.get('otrosGastos')?.value);
    const fuente = String(
      this.form.get('fuenteOtrosGastos')?.value ?? ''
    ).toLowerCase();
    if (otros > 0 && (!fuente || fuente.includes('general'))) {
      this.form
        .get('gastosGeneralesMonto')
        ?.setValue(formatPesosCop(otros), { emitEvent: false });
      this.form.get('otrosGastos')?.setValue('', { emitEvent: false });
      this.form.get('fuenteOtrosGastos')?.setValue('', { emitEvent: false });
    }
  }

  private precargarDocumentosCargados(): void {
    this.solicitudService.cargarDocumentosSolicitud().subscribe((data) => {
      this.aplicarDocumentosDesdeSolicitud(data);
      markInvalidControlsTouched(this.form);
      this.solicitudService.persistirCamposProgreso().subscribe();
      this.markSectionBaseline();
      this.scheduleBaselineAfterUiSettle();
      this.maybeOpenIntroModal();
    });
  }

  private aplicarDocumentosDesdeSolicitud(data: {
    archivos: UploadedTipoDocumento[];
    fotos: UploadedTipoDocumento[];
  }): void {
    const filtered = intersectSeccionDocsConSolicitud(
      this.documentosService.documentos,
      data,
      'sobre-ingresos',
      { juridica: this.esJuridica }
    );
    this.applyDocumentos(filtered);
    this.progress.replaceDocumentosDeSeccion('sobre-ingresos', filtered);
    const allDocs = [
      ...this.documentosPasoActivos,
      ...this.documentosPasoInfo,
    ];
    for (const item of [...data.archivos, ...data.fotos]) {
      const def = allDocs.find(
        (d) => Number(d.idTipoDocumento ?? d.id) === Number(item.idTipoDocumento)
      );
      if (!def) {
        continue;
      }
      const files = (item.neoDocumentosSolicitudDTO ?? []).filter(
        (f) => !!f?.idDocumentoSolicitud
      );
      const slots = slotsForDoc(def);
      slots.forEach((slot, idx) => {
        const uploaded = files[idx];
        if (!uploaded) {
          this.docsFiles[slot.slotKey] = emptyArchivoSlot();
          return;
        }
        this.docsFiles[slot.slotKey] = slotStateFromUploaded(
          uploaded,
          uploaded.nombreDocumento || 'Documento'
        );
      });
      this.syncDocProgressControl(def);
    }
    this.hydrateDeclaraRentaFromDoc();
    this.applySegmentoValidators();
    this.rebuildSteps();
    this.syncProgress();
  }

  /** Si ya hay archivo de renta y no hay respuesta, asume Sí. */
  private hydrateDeclaraRentaFromDoc(): void {
    if (this.esExpress) {
      return;
    }
    const doc = this.docDeclaracionRenta;
    if (!doc || this.form.get('declaraRenta')?.value) {
      return;
    }
    if (this.hasDoc(doc.valueKey)) {
      this.form.get('declaraRenta')?.setValue('si', { emitEvent: false });
    }
  }

  private serializeSectionState(): string {
    return serializePerfilSectionState(
      this.form.getRawValue(),
      this.docsFiles
    );
  }

  private markSectionBaseline(): void {
    this.dirtyTracker.markBaseline(() => this.serializeSectionState());
    this.form.markAsPristine();
  }

  private scheduleBaselineAfterUiSettle(): void {
    setTimeout(() => this.markSectionBaseline(), 150);
  }

  private resetDirtyAfterSave(): void {
    this.markSectionBaseline();
  }

  private guardarPasoYAvanzar(): void {
    this.saving = true;
    this.guardarPatchActual()
      .pipe(
        switchMap((res) => {
          const recargarPorVentas =
            this.esJuridica && this.currentStepId === 'activos-soportes';
          if (!res.exitoso || !recargarPorVentas) {
            return of({ res, recargado: false });
          }
          // PAR-117 usa valorVentas persistido: recargar tipos de la solicitud.
          return this.solicitudService.cargarDocumentosSolicitud().pipe(
            map((data) => {
              this.aplicarDocumentosDesdeSolicitud(data);
              this.solicitudService.persistirCamposProgreso().subscribe();
              return { res, recargado: true };
            })
          );
        })
      )
      .subscribe(({ res }) => {
        this.saving = false;
        if (!res.exitoso) {
          void this.sweetAlert.errorModal(
            'Error!',
            true,
            res.error || 'No se pudo guardar la información'
          );
          return;
        }
        this.resetDirtyAfterSave();
        if (this.isLastStep) {
          this.goToNextSection();
          return;
        }
        this.currentStep += 1;
        if (this.currentStepId === 'flujo-caja') {
          this.panelIngresosOpen = true;
          this.panelGastosOpen = false;
        }
        this.scrollFormToTop();
      });
  }

  private guardarPatchActual() {
    const v = this.form.getRawValue();
    const guardarObservaciones = !!this.form.get('observaciones')?.dirty;
    const num = (raw: unknown) => parsePesosCop(raw) || 0;
    const esGenerales = v.modoGastos === 'generales';
    const totalGenerales = num(v.gastosGeneralesMonto);
    const patch: Parameters<PerfilSolicitudService['guardarBorrador']>[0] = {
      anioActivos: v.anioActivos,
      nivelActivos: num(v.valorActivos),
      declaraRenta:
        v.declaraRenta === 'si'
          ? true
          : v.declaraRenta === 'no'
            ? false
            : null,
      flujoCajaMesCorteInformacion: v.mesReporte || '',
      flujoCajaVentas: num(v.ventasUltimoMes),
      flujoCajaOtrosIngresos: num(v.otrosIngresos),
      flujoCajaFuenteOtrosIngresos: v.fuenteOtrosIngresos || '',
      flujoCajaPagoProveedores: esGenerales ? 0 : num(v.pagoProveedores),
      flujoCajaArriendo: esGenerales ? 0 : num(v.arriendo),
      flujoCajaAdministracion: esGenerales ? 0 : num(v.administracion),
      flujoCajaServiciosPublicos: esGenerales
        ? 0
        : num(v.serviciosPublicosNegocio),
      flujoCajaSueldoPropio: esGenerales ? 0 : num(v.sueldoPropio),
      flujoCajaSueldoEmpleados: esGenerales ? 0 : num(v.sueldoEmpleados),
      flujoCajaPlanCelular: esGenerales ? 0 : num(v.planCelular),
      flujoCajaTarjetaCredito: esGenerales ? 0 : num(v.tarjetaCredito),
      flujoCajaCuotaCreditos: esGenerales ? 0 : num(v.cuotaCreditos),
      flujoCajaSaldoDeudas: esGenerales ? 0 : num(v.saldoDeudas),
      // En generales el total se persiste en otros gastos (no hay columna aparte).
      flujoCajaOtrosGastos: esGenerales ? totalGenerales : num(v.otrosGastos),
      flujoCajaFuenteOtrosGastos: esGenerales
        ? 'Gastos generales'
        : v.fuenteOtrosGastos || '',
      autorizacionCentrales: !!v.autorizacionCentrales,
    };
    if (this.esJuridica) {
      patch.valorVentas = num(v.ventasUltimoAnio);
    }
    const guardarBorrador$ = this.solicitudService.guardarBorrador(patch);
    if (!guardarObservaciones) {
      return guardarBorrador$;
    }
    return guardarBorrador$.pipe(
      switchMap((res) => {
        if (!res.exitoso) {
          return of(res);
        }
        return this.solicitudService.guardarObservacionesMicroempresario(
          String(v.observaciones ?? '').trim()
        );
      })
    );
  }

  private toInputDate(value: unknown): string {
    if (!value) {
      return '';
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }
    try {
      return new Date(value as string).toISOString().slice(0, 10);
    } catch {
      return '';
    }
  }
}
